const propertyText = 'text' as const;
const propertyBoolean = 'boolean' as const;
const propertyNumber = 'number' as const;

type PropertyType =
  | typeof propertyText
  | typeof propertyBoolean
  | typeof propertyNumber;
interface PropertyTypeToRealTypeMape {
  [propertyText]: string;
  [propertyBoolean]: boolean;
  [propertyNumber]: number;
}
interface Prop {
  type?: PropertyType;
  description?: string;
  defaultValue?: unknown;
}
interface Functions {
  description?: string;
}
interface Event {
  description?: string;
}
type PropertiesConfig = Record<string, Prop>;
type EventsConfig = Record<string, Event>;
type FunctionsConfig = Record<string, Functions>;

// Extract* types are used as helpers to extract "real" API of widget from config that passes inside defineApi.
type ExtractWidgetProps<Q extends PropertiesConfig> =
  {
// prettier-ignore
-readonly[Property in keyof Q]: Q[Property]['type'] extends PropertyType
  ? PropertyTypeToRealTypeMape[Q[Property]['type']]
  : Q[Property]['defaultValue'];
};
type ExtractWidgetFunctions<T extends FunctionsConfig> = {
  [Property in keyof T]: (...params: any[]) => any;
};
type ExtractWidgetEvents<T extends EventsConfig> = {
  [Property in keyof T as `on${Capitalize<string & Property>}`]: (
    cb: (...args: any[]) => void,
  ) => void;
};

type DefineAPIFn = <
  T extends PropertiesConfig = {},
  E extends EventsConfig = {},
  Q extends FunctionsConfig = {},
>(config: {
  props?: T;
  functions?: Q;
  events?: E;
}) => {
  _types: {
    flat: ExtractWidgetProps<T> &
      ExtractWidgetFunctions<Q> &
      ExtractWidgetEvents<E> &
      Record<string, never>;
    props: ExtractWidgetProps<T>;
    events: {
      [Property in keyof E]: any;
    };
    functions: ExtractWidgetFunctions<Q>;
  };
  properties: any[];
  events: any[];
  functions: any[];
};

export const WidgetPropertyType = {
  STRING: propertyText,
  BOOLEAN: propertyBoolean,
  NUMBER: propertyNumber,
};

export const defineAPI: DefineAPIFn = ({
  props = {} as PropertiesConfig,
  functions = {} as FunctionsConfig,
  events = {} as EventsConfig,
}) => {
  return {
    properties: Object.entries(props).map(([property, value]) => ({
      name: property,
      defaultValue: value.defaultValue,
    })),
    events: Object.keys(events).map((event) => ({
      name: event,
    })),
    functions: Object.keys(functions).map((fn) => ({
      name: fn,
    })),
    _types: null as any,
  };
};
